import React from 'react';
import { Link } from 'react-router-dom';
import styles from './PrivacyPolicy.module.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>Privacy Policy</h1>
        <p className={styles.updated}>Last updated: March 2026</p>
        <hr className={styles.divider} />

        <section className={styles.section}>
          <h2 className={styles.heading}>Overview</h2>
          <p>
            Njordheim is a browser-based RPG. We value your privacy and collect only what is
            strictly necessary to operate the game. We do not sell, share, or monetise your
            personal data.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.heading}>What we collect</h2>
          <ul className={styles.list}>
            <li>
              <strong>Email address</strong>, collected when you register with email/password.
              Used only for account authentication and, if you request it, password recovery.
            </li>
            <li>
              <strong>Discord ID</strong>, collected if you choose to sign in via Discord OAuth.
              Used only to identify your account across sessions. We do not access your Discord
              messages, servers, or any other Discord data beyond your public profile ID.
            </li>
            <li>
              <strong>Game data</strong>, all in-game activity (characters, battles, items, chat)
              is stored to operate the game. This is not personal data in a regulatory sense but
              is tied to your account.
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.heading}>What we do NOT collect</h2>
          <ul className={styles.list}>
            <li>Real name, address, or phone number</li>
            <li>Payment information (any purchases are handled entirely by PayPal)</li>
            <li>Tracking cookies, advertising identifiers, or analytics profiles</li>
            <li>Browser fingerprints or device identifiers beyond what a normal session cookie requires</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.heading}>Cookies &amp; sessions</h2>
          <p>
            We use a single server-side session cookie to keep you logged in. This cookie contains
            only a session token, no personal information is stored inside the cookie itself.
            The session expires when you log out or after a period of inactivity.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.heading}>Data storage</h2>
          <p>
            All data is stored in a PostgreSQL database hosted by our server provider. We apply
            reasonable technical measures to protect the data. Access is restricted to the site
            operators.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.heading}>Your rights</h2>
          <p>
            You may request deletion of your account and all associated personal data at any time
            by contacting us via the in-game mail system or through our Discord server. We will
            process your request within 30 days.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.heading}>Third-party services</h2>
          <ul className={styles.list}>
            <li>
              <strong>Discord OAuth</strong>, only used if you choose to log in with Discord.
              Governed by{' '}
              <a
                href="https://discord.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                Discord's Privacy Policy
              </a>.
            </li>
            <li>
              <strong>PayPal</strong>, payment processing for optional in-game purchases.
              Governed by{' '}
              <a
                href="https://www.paypal.com/webapps/mpp/ua/privacy-full"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                PayPal's Privacy Policy
              </a>.
              We do not receive or store your payment details.
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.heading}>Contact</h2>
          <p>
            If you have any questions about this policy, reach us through the Njordheim Discord
            server or via in-game mail.
          </p>
        </section>

        <Link to="/" className={styles.backLink}>← Back to Njordheim</Link>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
